import {
  aiSidekickChatReducer,
  aiSidekickConsentReducer,
  aiSidekickReducer,
} from '@brightdrop/ai-sidekick';
import { sidekickRtkClient } from '@brightdrop/ai-sidekick/api';
import {
  entitlementsReducer,
  entitlementsRtkClient,
  publicApi,
} from '@gm-commercial/entitlements-client';
import {
  rtkAssetsClient,
  rtkFleetPartnersClient,
  rtkOrdertrackingClient,
  rtkOrgClient,
  rtkPermissionsClient,
  rtkSafetyClient,
  rtkUsersClient,
} from '@gm-commercial/rtk-clients';
import type { ReducersMapObject } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { rtkClient } from '~/api';
import { addVehiclesReducer } from '~/features/assets/addVehicles/addVehiclesSlice';
import { assetsReducer } from '~/features/assets/assetsSlice';
import { activationProgressReducer } from '~/features/catalog/shared/components/activationProgress/state/activationProgressSlice';
import {
  deviceOrderConfirmationPersistTransform,
  deviceOrderPreviewPersistTransform,
} from '~/features/catalog/state/catalogPersist.transform';
import { catalogReducer } from '~/features/catalog/state/catalogSlice.reducers';
import { fleetsReducer } from '~/features/fleets/fleetsSlice';
import { vehicleOrdersReducer } from '~/features/orders/vehicleOrdersSlice';
import { organizationsReducer } from '~/features/organizations/organizationsSlice';
import { usersReducer } from '~/features/users/usersSlice';
import { peripheralsReducer } from '~/routes/organizations/devices/peripheral/state/peripheralsSlice';
import { csDashboardReducer } from '~/routes/organizations/home/dashboard/csPlatform/csDashboardSlice';
import { safetyEventHistoryReducer } from '~/routes/organizations/safety/state/safetyEventHistorySlice';
import { tripHistoryReducer } from '~/routes/organizations/vehicles/details/tripHistory/state/tripHistorySlice';
import chargeStationsReducer from '~/routes/organizations/vehicles/map/chargeStations/state/chargeStationsSlice';
import { alertsReducer } from '~/store/alerts/alertsSlice';
import { featureFlagsReducer } from '~/store/featureFlags/featureFlagsSlice';
import { fleetMapReducer } from '~/store/fleetMap/fleetMapSlice';
import { profileReducer } from '~/store/profile/profileSlice';
import { camerasSettingsReducer } from '~/store/settings/camerasSettingsSlice';

const profilePersistConfig = {
  key: 'profile',
  storage,
  whitelist: ['rowsPerPage', 'actingProfile'],
};

const catalogPersistConfig = {
  key: 'catalog',
  storage,
  whitelist: [
    'orderPreview',
    'orderConfirmationDetails',
    'deviceOrderConfirmation',
    'deviceOrderPreview',
  ],
  transforms: [
    deviceOrderConfirmationPersistTransform,
    deviceOrderPreviewPersistTransform,
  ],
};

/**
 * Reducers mounted on the store at creation time.
 *
 * NOTE: Slices whose module graph is heavy and only needed behind a specific
 * route should be omitted here and injected lazily via the reducer manager
 * (see `reducerManager.ts` and `injectReducer` in `store.ts`). The `geofence`
 * slice is intentionally excluded because it pulls `@brightdrop/map-ui` +
 * `mapbox-gl` — it is injected when a map route is lazily loaded.
 */
export const staticReducers = {
  activationProgress: activationProgressReducer,
  addVehicles: addVehiclesReducer,
  // Sidekick state is split across three package-owned keys: `aiSidekick`
  // (chrome: drawer open/close, dock size, chip selection), `aiSidekickChat`
  // (messages, streaming, feedback, errors), and `aiSidekickConsent`
  // (legal-disclaimer gate). The dock mounts outside the router, so these
  // are static rather than route-injected.
  aiSidekick: aiSidekickReducer,
  aiSidekickChat: aiSidekickChatReducer,
  aiSidekickConsent: aiSidekickConsentReducer,
  alerts: alertsReducer,
  [rtkClient.reducerPath]: rtkClient.reducer,
  [sidekickRtkClient.reducerPath]: sidekickRtkClient.reducer,
  assets: assetsReducer,
  camerasSettings: camerasSettingsReducer,
  chargeStations: chargeStationsReducer,
  csDashboard: csDashboardReducer,
  entitlements: entitlementsReducer,
  [entitlementsRtkClient.reducerPath]: entitlementsRtkClient.reducer,
  [publicApi.reducerPath]: publicApi.reducer,
  featureFlags: featureFlagsReducer,
  fleets: fleetsReducer,
  fleetMap: fleetMapReducer,
  organizations: organizationsReducer,
  catalog: persistReducer(catalogPersistConfig, catalogReducer),
  peripherals: peripheralsReducer,
  profile: persistReducer(profilePersistConfig, profileReducer),
  safetyEventHistory: safetyEventHistoryReducer,
  tripHistory: tripHistoryReducer,
  users: usersReducer,
  vehicleOrders: vehicleOrdersReducer,
  [rtkOrgClient.reducerPath]: rtkOrgClient.reducer,
  [rtkAssetsClient.reducerPath]: rtkAssetsClient.reducer,
  [rtkFleetPartnersClient.reducerPath]: rtkFleetPartnersClient.reducer,
  [rtkOrdertrackingClient.reducerPath]: rtkOrdertrackingClient.reducer,
  [rtkPermissionsClient.reducerPath]: rtkPermissionsClient.reducer,
  [rtkSafetyClient.reducerPath]: rtkSafetyClient.reducer,
  [rtkUsersClient.reducerPath]: rtkUsersClient.reducer,
} satisfies ReducersMapObject;
